import { type Ref, useCallback, useEffect, useRef, useState } from 'react'
import './masthead.css'

/**
 * The masthead an org drew for its public home, drawn in a frame of its own.
 *
 * The frame keeps the org's styles off the page and the page's off the
 * masthead, and runs no script: the sandbox grants the same origin only so the
 * page can read the drawing's height, which a frame cannot report by itself.
 * Its media queries answer the frame's width, which is the page's measure.
 *
 * The height follows the drawing's own box rather than the frame's, so a face
 * or an image settling after load grows the frame with it. The page's heading
 * stays in the page: what the frame holds is a separate document to a reader
 * walking the headings. On a home page that heading is the org's name, held
 * for the reader alone; a page with a heading of its own — a fund's — names
 * the masthead's region instead and adds none.
 */
export function Masthead({
  html,
  title,
  ref,
  heading = true,
  bleed = true,
}: {
  html: string
  title: string
  ref?: Ref<HTMLElement>
  /** Whether the masthead carries the page's heading. */
  heading?: boolean
  /** Whether the page runs edge to edge, so the masthead keeps its own
   *  measure; on a page held to the measure already it fills that. */
  bleed?: boolean
}) {
  const frame = useRef<HTMLIFrameElement>(null)
  const watch = useRef<ResizeObserver | null>(null)
  const [height, setHeight] = useState<number | null>(null)

  const follow = useCallback(() => {
    const root = frame.current?.contentDocument?.documentElement
    if (!root) return
    watch.current?.disconnect()
    watch.current = new ResizeObserver(() => setHeight(root.scrollHeight))
    watch.current.observe(root)
    setHeight(root.scrollHeight)
  }, [])

  useEffect(() => () => watch.current?.disconnect(), [])

  const frameEl = (
    <iframe
      ref={frame}
      className="masthead__frame"
      title={title}
      sandbox="allow-same-origin"
      srcDoc={html}
      onLoad={follow}
      style={height === null ? undefined : { height }}
    />
  )
  return (
    <section
      className="masthead"
      aria-labelledby={heading ? 'home-hero' : undefined}
      aria-label={heading ? undefined : title}
      ref={ref}
    >
      {heading ? (
        <h1 id="home-hero" className="sr-only">
          {title}
        </h1>
      ) : null}
      {bleed ? <div className="home__measure">{frameEl}</div> : frameEl}
    </section>
  )
}
